import { SyntheticEvent, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  getAnnotationToolAdminProjectsPath,
  useAppDispatch,
  useAppSelector,
} from '@verantos-dev/core-annotation-tool';

import { useGetProjectDetailsQuery } from './api/adminProjectsApiSlice';
import { ADMIN_PROJECT_TABS, PROJECT_STATUS_INACTIVE, type AdminProjectTabType } from './constants';
import {
  clearDefaultAllocationModeEdit,
  setDefaultAllocationModeEdit,
  setSelectedTab,
} from './state/adminProjectSlice';
import { selectSelectedTab } from './state/selector';

export const useProjectDetails = () => {
  const { projectId = '' } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTab = useAppSelector(selectSelectedTab) ?? ADMIN_PROJECT_TABS.OVERVIEW;

  const handleTabChange = (event: SyntheticEvent, value: AdminProjectTabType) => {
    event.preventDefault();
    dispatch(setSelectedTab(value as AdminProjectTabType));
  };

  const handleBackToProjects = () => {
    navigate(getAnnotationToolAdminProjectsPath());
  };

  const initialEditMode = searchParams.get('edit') === '1';
  const hasConsumedEditParam = useRef(false);

  // `?edit=1` is a one-time deep-link trigger, not persistent state: once it has set the
  // initial edit mode, drop it from the URL so a later refresh (or cancelling out of edit
  // mode) lands back in read mode instead of re-entering edit mode via the stale param.
  useEffect(() => {
    if (hasConsumedEditParam.current || !initialEditMode) {
      return;
    }
    hasConsumedEditParam.current = true;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('edit');
    setSearchParams(nextParams, { replace: true });
  }, [initialEditMode, searchParams, setSearchParams]);

  const {
    data: projectDetailsData,
    isLoading: isProjectDetailsLoading,
    isFetching: isProjectDetailsFetching,
  } = useGetProjectDetailsQuery(
    { projectId: projectId as string },
    { skip: !projectId || projectId === '' }
  );

  useEffect(() => {
    if (projectDetailsData?.data?.defaultAllocationMode ?? false) {
      dispatch(setDefaultAllocationModeEdit());
    } else {
      dispatch(clearDefaultAllocationModeEdit());
    }
  }, [projectDetailsData?.data?.defaultAllocationMode]);

  return {
    projectId,
    initialEditMode,
    isProjectInactive: projectDetailsData?.data?.status === PROJECT_STATUS_INACTIVE,
    projectName: projectDetailsData?.data?.basics?.projectName ?? '',
    createdOn: projectDetailsData?.data?.createdAt ?? '',
    startedOn: projectDetailsData?.data?.startDate ?? '',
    completedOn: projectDetailsData?.data?.completedDate ?? '',
    isProjectDetailsLoading: isProjectDetailsLoading || isProjectDetailsFetching,
    selectedTab,
    handleTabChange,
    handleBackToProjects,
  };
};
